import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import {
  ClipboardList,
  FileText,
  AlertCircle,
  Clock,
  Phone,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useGetFieldForceAssignments } from "@features/wx/fieldForce/hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

interface AssignmentsTabProps {
  technicianId: string;
}

export const AssignmentsTab: React.FC<AssignmentsTabProps> = ({
  technicianId,
}) => {
  const { id, ff_no } = useParams<{ id: string; ff_no: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const itemsPerPage = 10;

  // ✅ Get filters from URL
  const getFiltersFromUrl = () => {
    const page = parseInt(searchParams.get("assignmentsPage") || "1");
    const searchTerm = searchParams.get("assignmentsSearch") || "";

    return {
      page,
      searchTerm,
    };
  };

  const [filters, setFilters] = useState(getFiltersFromUrl);
  const [searchInput, setSearchInput] = useState(filters.searchTerm);

  // ✅ Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams]);

  // ✅ API call with page and search parameters
  const {
    data: assignmentsResponse,
    isLoading,
    error,
  } = useGetFieldForceAssignments(ff_no, filters.page, filters.searchTerm);

  // ✅ Calculate pagination info
  const pagination = useMemo(() => {
    const totalItems = assignmentsResponse?.count || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = (filters.page - 1) * itemsPerPage + 1;
    const endItem = Math.min(filters.page * itemsPerPage, totalItems);

    return {
      currentPage: filters.page,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    };
  }, [assignmentsResponse, filters.page, itemsPerPage]);

  // ✅ Page change handler
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (
        newPage !== filters.page &&
        newPage >= 1 &&
        newPage <= pagination.totalPages
      ) {
        const params = new URLSearchParams(searchParams);
        params.set("assignmentsPage", newPage.toString());
        setSearchParams(params);
      }
    },
    [filters.page, pagination.totalPages, searchParams, setSearchParams]
  );

  // ✅ Search handler
  const handleSearch = useCallback(
    (searchTerm: string) => {
      const params = new URLSearchParams(searchParams);

      if (searchTerm.trim()) {
        params.set("assignmentsSearch", searchTerm);
      } else {
        params.delete("assignmentsSearch");
      }

      // Reset to page 1 when searching
      params.set("assignmentsPage", "1");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );
  // Debounce the search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.searchTerm) {
        handleSearch(searchInput);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput, filters.searchTerm, handleSearch]);

  // Get assignments array from response
  const assignments = assignmentsResponse?.results || [];

  // ✅ Pagination Controls Component
  const PaginationControls = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-center gap-2 mt-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            handlePageChange(Math.max(1, pagination.currentPage - 1))
          }
          disabled={pagination.currentPage === 1 || isLoading}
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        {/* Show page numbers dynamically */}
        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
          // Calculate which pages to show
          const startPage = Math.max(1, pagination.currentPage - 2);
          const endPage = Math.min(pagination.totalPages, startPage + 4);
          const adjustedStartPage = Math.max(1, endPage - 4);

          const page = adjustedStartPage + i;
          if (page > pagination.totalPages) return null;

          return (
            <Button
              key={page}
              variant={pagination.currentPage === page ? "default" : "outline"}
              size="sm"
              onClick={() => handlePageChange(page)}
              disabled={isLoading}
              className={`w-8 h-8 p-0 ${
                pagination.currentPage === page
                  ? "bg-blue-600 text-white"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {page}
            </Button>
          );
        })}

        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            handlePageChange(
              Math.min(pagination.totalPages, pagination.currentPage + 1)
            )
          }
          disabled={
            pagination.currentPage === pagination.totalPages || isLoading
          }
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className=" space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="card-heading-text flex items-center">
          <ClipboardList className="mr-2 h-5 w-5 text-blue-500" />
          Current Assignments
          {pagination.totalItems > 0 && (
            <span className="text-blue-600 ml-2">
              ({pagination.totalItems} total)
            </span>
          )}
        </h3>
      </div>

      {/* ✅ Search Input */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by job card, SOP name, or status..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* ✅ Show pagination info */}
      {pagination.totalItems > 0 && (
        <div className="text-sm text-gray-600">
          Showing {pagination.startItem}-{pagination.endItem} of{" "}
          {pagination.totalItems} assignments
        </div>
      )}

      {/* ✅ Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading assignments...</span>
        </div>
      )}

      {/* ✅ Assignments Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {assignments?.map((tx, i) => (
            <Card
              key={i}
              className="overflow-hidden border-l-4 shadow-sm hover:shadow-md transition-shadow"
              style={{
                borderLeftColor: tx.statusDisplay,
              }}
            >
              <CardHeader className="p-4 pb-3 flex flex-row justify-between items-start bg-gradient-to-r from-slate-50 to-white">
                <div>
                  <div className="flex gap-2 items-center">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">{tx.jobCardNo}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {tx.sopName}
                  </p>
                </div>

                <Badge className="bg-white-100 text-red-800 border border-red-200 ">
                  {tx.statusDisplay}
                </Badge>
              </CardHeader>
              <CardContent className="p-4 pt-3 border-t">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">Assigned:</p>
                    <p className="font-medium">{tx.assignDate}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Priority:</p>
                    <p
                      className={`font-medium flex items-center ${
                        tx.priorityDisplay === "Critical"
                          ? "text-purple-700" // intense & urgent
                          : tx.priorityDisplay === "High"
                          ? "text-red-600"
                          : tx.priorityDisplay === "Medium"
                          ? "text-amber-600"
                          : tx.priorityDisplay === "Low"
                          ? "text-green-600"
                          : "text-gray-500" // Undefined or fallback
                      }`}
                    >
                      <AlertCircle size={12} className="mr-1" />
                      {tx.priorityDisplay}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Expected:</p>
                    <p className="font-medium">{tx.expectedEndDate}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t flex justify-end">
                  <Button
                    size="sm"
                    onClick={() =>
                      tx.sopAssociationTypeDisplay === "Meter"
                        ? navigate(
                            `/service-orders/dispatcher/assigned-meter-service-order/${tx.id}`
                          )
                        : navigate(
                            `/service-orders/dispatcher/assigned-consumer-service-order/${tx.id}`
                          )
                    }
                  >
                    <Eye size={14} className="mr-1" />
                    View SOP
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ✅ Empty State */}
      {!isLoading && assignments?.length === 0 && (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed">
          <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium mb-2">
            {filters.searchTerm
              ? `No assignments found matching "${filters.searchTerm}"`
              : "No Active Assignments"}
          </h4>
          <p className="text-muted-foreground">
            {filters.searchTerm
              ? "Try adjusting your search terms."
              : "This technician has no active or overdue service orders."}
          </p>
        </div>
      )}

      {/* ✅ Pagination Controls */}
      <PaginationControls />
    </div>
  );
};
